/**
 * ManuscriptPolishPanel.jsx
 *
 * Whole-Manuscript Polish — runs a per-chapter AI prose polish pass.
 * Authors control what gets committed via Accept / Skip per chapter,
 * or bulk-accept all chapters in one click.
 *
 * Two modes (user selects):
 *   • Chapter-by-chapter — results appear as each chapter finishes;
 *     author can Accept/Skip while polishing is still in progress.
 *   • Batch — all chapters polished first, then author reviews summary
 *     diff and bulk-accepts.
 *
 * Opened as a Dialog from the Tools menu.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
  SkipForward,
  Sparkles,
  Wand2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 3000;

const STYLE_OPTIONS = [
  { value: 'literary',   label: 'Literary',   desc: 'Precise, evocative language — varied sentence rhythm, sharp sensory detail' },
  { value: 'commercial', label: 'Commercial',  desc: 'Faster pacing — cut passive voice, tighten dialogue, punch up momentum' },
  { value: 'genre',      label: 'Genre',       desc: 'Vivid action, crisp dialogue, visceral stakes — maximise immediacy' },
  { value: 'minimal',    label: 'Minimal',     desc: 'Light copyedit only — grammar, repetition, awkward phrasing. Voice untouched.' },
];

const STATUS_CFG = {
  pending:  { label: 'Queued',     cls: 'bg-muted text-muted-foreground' },
  running:  { label: 'Polishing',  cls: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300' },
  ready:    { label: 'Ready',      cls: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300' },
  accepted: { label: 'Accepted',   cls: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300' },
  skipped:  { label: 'Skipped',    cls: 'bg-muted text-muted-foreground' },
  failed:   { label: 'Failed',     cls: 'bg-rose-100 text-rose-700 border-rose-200' },
};

function DeltaBadge({ delta }) {
  if (delta == null) return null;
  const abs = Math.abs(delta);
  if (abs < 5) return <span className="text-[13px] text-muted-foreground">±{abs}w</span>;
  if (delta > 0) return <span className="text-[13px] text-emerald-600">+{delta}w</span>;
  return <span className="text-[13px] text-rose-500">{delta}w</span>;
}

function ChapterRow({ chapter, jobId, projectId, onUpdate, mode }) {
  const [expanded, setExpanded]       = useState(false);
  const [accepting, setAccepting]     = useState(false);
  const [skipping, setSkipping]       = useState(false);

  const status = chapter.status || 'pending';
  const cfg    = STATUS_CFG[status] || STATUS_CFG.pending;
  const isReady = status === 'ready';

  const handleAccept = async () => {
    if (!isReady || accepting) return;
    setAccepting(true);
    try {
      await axios.post(
        `${API}/api/projects/${projectId}/polish/jobs/${jobId}/chapters/${chapter.chapter_id}/accept`,
        {},
        { withCredentials: true },
      );
      onUpdate(chapter.chapter_id, 'accepted');
      toast.success(`"${chapter.chapter_title}" committed.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Accept failed.');
    } finally { setAccepting(false); }
  };

  const handleSkip = async () => {
    if (skipping) return;
    setSkipping(true);
    try {
      await axios.post(
        `${API}/api/projects/${projectId}/polish/jobs/${jobId}/chapters/${chapter.chapter_id}/skip`,
        {},
        { withCredentials: true },
      );
      onUpdate(chapter.chapter_id, 'skipped');
    } catch (e) {
      toast.error('Skip failed.');
    } finally { setSkipping(false); }
  };

  return (
    <div className={cn(
      'rounded-xl border border-border overflow-hidden transition-colors',
      status === 'accepted' && 'border-emerald-200 dark:border-emerald-800/50',
      status === 'failed' && 'border-rose-200',
    )}>
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="text-[13.5px] text-muted-foreground tabular-nums w-5 shrink-0">
          {chapter.chapter_number ?? '—'}
        </span>
        <span className="flex-1 text-[13px] font-medium truncate">
          {chapter.chapter_title || 'Untitled'}
        </span>

        <div className="flex items-center gap-2 shrink-0">
          <DeltaBadge delta={chapter.delta_words} />
          <Badge className={cn('text-[13px] h-5 px-2', cfg.cls)}>{cfg.label}</Badge>

          {/* Actions — only for ready chapters in chapter-by-chapter mode */}
          {(mode === 'chapter_by_chapter' || status === 'ready') && isReady && (
            <>
              <Button
                data-testid={`polish-accept-${chapter.chapter_id}`}
                size="sm"
                className="h-6 text-[13px] px-2 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleAccept}
                disabled={accepting || skipping}
              >
                {accepting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                Accept
              </Button>
              <Button
                data-testid={`polish-skip-${chapter.chapter_id}`}
                size="sm"
                variant="outline"
                className="h-6 text-[13px] px-2 gap-1"
                onClick={handleSkip}
                disabled={accepting || skipping}
              >
                {skipping ? <Loader2 className="h-3 w-3 animate-spin" /> : <SkipForward className="h-3 w-3" />}
                Skip
              </Button>
            </>
          )}

          {status === 'accepted' && <Check className="h-3.5 w-3.5 text-emerald-600" />}

          {/* Expand toggle for ready chapters */}
          {(status === 'ready' || status === 'accepted') && chapter.polished && (
            <button
              className="text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setExpanded(v => !v)}
              data-testid={`polish-expand-${chapter.chapter_id}`}
            >
              {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>

      {/* Before / after preview */}
      {expanded && chapter.polished && (
        <div className="border-t border-border grid grid-cols-2 gap-0 bg-muted/10">
          <div className="px-4 py-3 border-r border-border">
            <p className="text-[13px] uppercase tracking-wider font-medium text-muted-foreground mb-2">Original</p>
            <p className="text-[13.5px] text-muted-foreground leading-relaxed whitespace-pre-wrap line-clamp-6">
              {(chapter.original || '').substring(0, 600)}{(chapter.original || '').length > 600 ? '…' : ''}
            </p>
          </div>
          <div className="px-4 py-3">
            <p className="text-[13px] uppercase tracking-wider font-medium text-emerald-600 mb-2">Polished</p>
            <p className="text-[13.5px] leading-relaxed whitespace-pre-wrap line-clamp-6">
              {(chapter.polished || '').substring(0, 600)}{(chapter.polished || '').length > 600 ? '…' : ''}
            </p>
          </div>
        </div>
      )}

      {status === 'failed' && chapter.error && (
        <div className="px-4 pb-3 text-[13.5px] text-rose-500 border-t border-rose-100">
          <AlertTriangle className="h-3 w-3 inline mr-1" />{chapter.error}
        </div>
      )}
    </div>
  );
}

export default function ManuscriptPolishPanel({ open, onClose, projectId, chapters: chapterList }) {
  const [style, setStyle]       = useState('literary');
  const [mode,  setMode]        = useState('chapter_by_chapter');
  const [jobId, setJobId]       = useState(null);
  const [job,   setJob]         = useState(null);     // full job object
  const [running, setRunning]   = useState(false);
  const [chapters, setChapters] = useState([]);       // sorted chapter results
  const [acceptingAll, setAcceptingAll] = useState(false);
  const pollRef = useRef(null);

  const stopPoll = () => { clearInterval(pollRef.current); pollRef.current = null; };

  // Reconnect to any active job when the panel opens
  useEffect(() => {
    if (!open || !projectId) return;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/projects/${projectId}/polish/jobs/active`,
          { withCredentials: true },
        );
        const data = r.data;
        if (data.active && data.job_id) {
          setJobId(data.job_id);
          setJob(data);
          setChapters(data.chapters_list || []);
          setRunning(data.status === 'running' || data.status === 'pending');
          startPoll(data.job_id);
        }
      } catch { /* no active job or network error — ignore */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const refreshJob = useCallback(async (jid) => {
    try {
      const r = await axios.get(
        `${API}/api/projects/${projectId}/polish/jobs/${jid}`,
        { withCredentials: true },
      );
      const data = r.data;
      setJob(data);
      setChapters(data.chapters_list || []);
      if (data.status === 'done' || data.status === 'failed' || data.status === 'cancelled') {
        stopPoll();
        setRunning(false);
        if (data.status === 'done') toast.success('Manuscript polish complete. Review and accept chapters below.');
        if (data.status === 'failed') toast.error(data.error || 'Polish job failed.');
      }
    } catch { stopPoll(); setRunning(false); }
  }, [projectId]);

  const startPoll = useCallback((jid) => {
    stopPoll();
    pollRef.current = setInterval(() => refreshJob(jid), POLL_MS);
  }, [refreshJob]);

  useEffect(() => { if (!open) stopPoll(); return stopPoll; }, [open]);

  const handleStart = async () => {
    if (!projectId || running) return;
    setRunning(true);
    setJob(null);
    setChapters([]);
    try {
      const r = await axios.post(
        `${API}/api/projects/${projectId}/polish/start`,
        { style, mode },
        { withCredentials: true },
      );
      setJobId(r.data.job_id);
      startPoll(r.data.job_id);
      toast.info('Polish started — results appear chapter by chapter.');
    } catch (e) {
      setRunning(false);
      toast.error(e?.response?.data?.detail || 'Failed to start polish job.');
    }
  };

  const handleUpdate = useCallback((cid, newStatus) => {
    setChapters(prev => prev.map(c =>
      c.chapter_id === cid ? { ...c, status: newStatus } : c,
    ));
  }, []);

  const handleAcceptAll = async () => {
    if (!jobId || acceptingAll) return;
    setAcceptingAll(true);
    try {
      const r = await axios.post(
        `${API}/api/projects/${projectId}/polish/jobs/${jobId}/accept-all`,
        {},
        { withCredentials: true },
      );
      await refreshJob(jobId);
      toast.success(`${r.data.accepted_count} chapter${r.data.accepted_count !== 1 ? 's' : ''} committed to manuscript.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk accept failed.');
    } finally { setAcceptingAll(false); }
  };

  const readyCount    = chapters.filter(c => c.status === 'ready').length;
  const acceptedCount = chapters.filter(c => c.status === 'accepted').length;
  const total         = job?.chapters_total ?? 0;
  const completed     = job?.chapters_completed ?? 0;
  const progress      = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isDone        = job?.status === 'done';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { stopPoll(); onClose(); } }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden"
        data-testid="manuscript-polish-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <Sparkles className="h-4.5 w-4.5 text-primary" />
            Whole-Manuscript Polish
          </DialogTitle>
          <p className="text-[13.5px] text-muted-foreground mt-0.5">
            AI polishes every chapter. You decide what stays — accept or skip per chapter.
          </p>
        </DialogHeader>

        <Separator />

        {/* Progress bar while running */}
        {(running || job) && (
          <div className="px-5 py-3 flex items-center gap-4">
            <div className="flex-1 space-y-1">
              <div className="flex justify-between text-[13px] text-muted-foreground">
                <span>
                  {running && !isDone
                    ? `Polishing… ${completed}/${total} chapters`
                    : isDone
                      ? `Complete — ${total} chapters polished`
                      : `${job?.status}`}
                </span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-1.5" />
            </div>
            {isDone && readyCount > 0 && (
              <Button
                data-testid="polish-accept-all-btn"
                size="sm"
                className="gap-1.5 text-[13.5px] bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                onClick={handleAcceptAll}
                disabled={acceptingAll}
              >
                {acceptingAll
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <CheckCheck className="h-3 w-3" />}
                Accept all ({readyCount})
              </Button>
            )}
            {(isDone || !running) && (
              <Button
                variant="outline" size="sm"
                className="text-[13.5px] shrink-0"
                onClick={() => { setJob(null); setChapters([]); setJobId(null); setRunning(false); }}
              >
                New run
              </Button>
            )}
          </div>
        )}

        <Separator />

        {/* Scrollable area: controls (pre-run) + chapter list */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-5 py-4 space-y-4">

            {/* Controls — only shown before a job starts */}
            {!running && !job && (
              <>
                {/* Style picker */}
                <div className="space-y-1.5">
                  <label className="text-[13px] uppercase tracking-wider font-medium text-muted-foreground">
                    Polish Style
                  </label>
                  <Select value={style} onValueChange={setStyle}>
                    <SelectTrigger className="h-9 text-[13px]" data-testid="polish-style-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STYLE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className="font-medium">{opt.label}</span>
                          <span className="ml-2 text-muted-foreground text-[13px]">{opt.desc}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Mode picker */}
                <div className="space-y-1.5">
                  <label className="text-[13px] uppercase tracking-wider font-medium text-muted-foreground">
                    Review Mode
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'chapter_by_chapter', label: 'Chapter by Chapter', desc: 'Accept or skip as each chapter finishes' },
                      { value: 'batch',              label: 'Batch Review',       desc: 'Polish all chapters, then review the full diff' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        data-testid={`polish-mode-${opt.value}`}
                        onClick={() => setMode(opt.value)}
                        className={cn(
                          'rounded-lg border p-3 text-left transition-colors',
                          mode === opt.value
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50 hover:bg-muted/30',
                        )}
                      >
                        <p className="text-[13px] font-medium">{opt.label}</p>
                        <p className="text-[13px] text-muted-foreground mt-0.5">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <Button
                    data-testid="polish-start-btn"
                    onClick={handleStart}
                    className="gap-2"
                  >
                    <Wand2 className="h-3.5 w-3.5" /> Polish Manuscript
                  </Button>
                  <span className="text-[13px] text-muted-foreground">
                    {chapterList?.length ?? 0} chapter{(chapterList?.length ?? 0) !== 1 ? 's' : ''} will be processed
                  </span>
                </div>

                <Separator />
              </>
            )}
            {chapters.length === 0 && running && (
              <div className="flex items-center gap-2 text-[13px] text-muted-foreground py-8 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
                Waiting for first chapter…
              </div>
            )}

            {chapters.length === 0 && !running && !job && (
              <div className="flex flex-col items-center gap-3 py-12 text-center">
                <Sparkles className="h-10 w-10 text-muted-foreground/30" />
                <p className="text-[13px] text-muted-foreground max-w-sm">
                  Choose a style and review mode above, then click <strong>Polish Manuscript</strong> to start.
                  Each chapter is polished by AI while preserving your voice.
                </p>
              </div>
            )}

            {chapters.map((chapter) => (
              <ChapterRow
                key={chapter.chapter_id}
                chapter={chapter}
                jobId={jobId}
                projectId={projectId}
                onUpdate={handleUpdate}
                mode={mode}
              />
            ))}

            {/* Summary stats when done */}
            {isDone && chapters.length > 0 && (
              <div className="mt-2 rounded-xl border border-border bg-muted/20 px-4 py-3 flex items-center gap-6 flex-wrap">
                <div>
                  <p className="text-[13px] uppercase tracking-wider text-muted-foreground font-medium">Accepted</p>
                  <p className="text-[14px] font-semibold text-emerald-600">{acceptedCount}</p>
                </div>
                <div>
                  <p className="text-[13px] uppercase tracking-wider text-muted-foreground font-medium">Pending</p>
                  <p className="text-[14px] font-semibold text-amber-600">{readyCount}</p>
                </div>
                <div>
                  <p className="text-[13px] uppercase tracking-wider text-muted-foreground font-medium">Skipped</p>
                  <p className="text-[14px] font-semibold text-muted-foreground">
                    {chapters.filter(c => c.status === 'skipped').length}
                  </p>
                </div>
                {acceptedCount > 0 && (
                  <p className="text-[13px] text-muted-foreground ml-auto">
                    Accepted chapters have been saved to your manuscript.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
